/**
 * API Client Context
 *
 * Provides an optional API client for fetching work items.
 * When provided (in widget context), uses the widget's configured API client.
 * When not provided (in main app), falls back to default workItemsApi.
 */

import { createContext, useContext } from 'react';
import type { WorkItem } from '../types/workItems';

/**
 * API Client interface for work item operations
 */
export interface ApiClient {
  /**
   * Fetch a single work item by ID
   * @param workItemId - The work item ID to fetch
   * @returns Promise resolving to the work item
   */
  getWorkItem: (workItemId: string) => Promise<WorkItem>;

  /**
   * Dismiss a work item
   * @param workItemId - The work item ID to dismiss
   * @param reason - Optional reason for dismissal
   * @returns Promise resolving to action response
   */
  dismissWorkItem: (workItemId: string, reason?: string) => Promise<unknown>;
}

/**
 * API Client Context
 * Null when not in widget context (main app uses default API)
 */
export const ApiClientContext = createContext<ApiClient | null>(null);

/**
 * Hook to get API client from context
 * @returns API client if in widget context, null otherwise
 */
export function useApiClient(): ApiClient | null {
  return useContext(ApiClientContext);
}
